import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Sparkles, Globe, Calendar, Check } from 'lucide-react';
import TiltCard from './TiltCard';

const SERVICE_ICONS = {
  1: { Icon: Globe, bg: 'bg-orange-500/20', color: 'text-orange-400' },
  2: { Icon: Sparkles, bg: 'bg-yellow-500/20', color: 'text-yellow-400' },
  3: { Icon: Calendar, bg: 'bg-amber-500/20', color: 'text-amber-400' },
};

const ServicesSection = ({ services }) => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="py-20 lg:py-32 bg-gray-950 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 rounded-full px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-orange-400 uppercase tracking-wide">Nos Services</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Création de sites web professionnels
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Des solutions clés en main pour votre présence en ligne. Prix fixes, délais maîtrisés, qualité garantie.
          </p>
        </div>

        {/* Services Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-12">
          {services.map((service) => {
            const { Icon, bg, color } = SERVICE_ICONS[service.id] || SERVICE_ICONS[1];
            return (
            <TiltCard key={service.id} glare={false} max={6} className="h-full rounded-xl">
            <Card
              className="relative overflow-hidden h-full transition-colors duration-300 hover:shadow-2xl hover:shadow-orange-500/10 bg-gray-900 border border-gray-700 hover:border-orange-400 group"
            >
              <CardHeader className="pt-8 pb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 ${bg} rounded-full flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${color}`} />
                  </div>
                  <CardTitle className="text-3xl font-bold text-white">
                    {service.name}
                  </CardTitle>
                </div>
                {service.inherit && (
                  <p className="text-orange-400 font-semibold text-base mb-3">
                    {service.inherit}
                  </p>
                )}
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <React.Fragment key={feature}>
                      <li className="flex items-center gap-2 text-gray-300 text-base">
                        <Check className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                      {service.featuresOr && i < service.features.length - 1 && (
                        <li className="text-gray-500 text-sm font-medium pl-6">ou</li>
                      )}
                    </React.Fragment>
                  ))}
                </ul>
              </CardHeader>

              <CardContent className="pb-8">
                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-bold bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                      {service.price}€
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    {service.note}
                  </p>
                </div>

                {/* CTA */}
                <button
                  onClick={() => scrollToSection('contact')}
                  className="w-full py-4 px-6 bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-full transition-all duration-300 border border-gray-700 hover:border-orange-500 group-hover:bg-gradient-to-r group-hover:from-orange-500 group-hover:to-yellow-500"
                >
                  Demander un devis
                </button>
              </CardContent>
            </Card>
            </TiltCard>
            );
          })}
        </div>

        {/* Bottom Note */}
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-3">
            💡 Tous mes sites incluent un abonnement pour la maintenance, l&apos;hébergement et la sécurité
          </p>
          <div className="flex items-center justify-center gap-3 text-gray-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2"/>
              <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2"/>
              <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2"/>
            </svg>
            <span className="text-lg font-semibold">+</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" strokeWidth="2"/>
              <line x1="12" y1="18" x2="12" y2="18" strokeWidth="2"/>
            </svg>
            <span className="text-lg">Sites optimisés pour ordinateurs et smartphones</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
